// Step 5 · Áttekintés és küldés: the page that makes the decision safe.
//
// Per country: who it goes to after the exclusions, whose name it goes out
// under, the subject(s), the local moment, and the letter as the sender will
// render it. Above that the checklist; under it the test send; at the bottom
// the one button, which says exactly what happens and asks for a second click.
//
// "Sending" a group is scheduling it (4.3: there is no send endpoint — the
// release is the group runner's). So the button issues `POST /review` (when
// the group is still a draft) and `POST /schedule`; "Most" schedules every
// country at its own current minute, which the runner releases on its next
// pass.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MailIcon, MonitorIcon, SendIcon, SmartphoneIcon } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { fetchTestSend, groupTestSend, requestGroupReview, scheduleGroup } from "@/lib/api";
import type { GroupCountryDetail, TemplateTestSend } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { DataTable } from "@/components/emc/DataTable";
import { Field } from "@/components/emc/Field";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, ErrorState, LoadingState } from "@/components/emc/states";
import { count } from "@/components/emc/format";
import { cn } from "@/lib/utils";
import { firstBlocker, sendingCountries } from "@/pages/campaigns/checks";
import type { Editor } from "@/pages/campaigns/editor/context";
import {
  COMPOSING,
  accountDayText,
  accountDays,
  dayFigures,
  draftStartText,
  draftStarts,
  forgetTiming,
  intoCountries,
  messageOf,
  scheduleOf,
  startsLine,
  startsText,
  subjectsSent,
} from "@/pages/campaigns/model";
import { CheckList } from "@/pages/campaigns/parts";
import { Country } from "@/components/emc/Flag";

export function ReviewStep({ editor }: { editor: Editor }) {
  const { summary, detail, checks, estimate, timing, identityOf } = editor;
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  // The numbers the decision is made on: ask for them when the page opens.
  useEffect(() => {
    if (summary.countries.length > 0 && !estimate.loading) void estimate.load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countries = sendingCountries(summary);
  const rows = summary.countries.filter((row) => countries.includes(row.country));
  const estimated = new Map((estimate.rows ?? []).map((row) => [row.country, row] as const));
  const recipients = rows.reduce((sum, row) => sum + (estimated.get(row.country)?.recipients ?? 0), 0);
  const zoneOf = (country: string) => identityOf(country)?.timezone ?? "UTC";
  const blocker = firstBlocker(checks);
  const composing = COMPOSING.has(summary.state);

  const when =
    timing.mode === "now"
      ? "most"
      : startsText(startsLine(draftStarts(timing, countries, zoneOf), zoneOf));
  // Until the estimate is in, the button does not claim a number.
  const who = estimate.rows === null ? "a címzettek száma számolódik" : `${count(recipients)} címzett`;
  const actionLabel =
    timing.mode === "now"
      ? `Kiküldés indítása ${intoCountries(countries.length)} — ${who}`
      : `Ütemezés ${intoCountries(countries.length)} — ${who}, ${when}`;

  async function launch() {
    // The checks are read again at the second click: a halt thrown while the
    // dialog was open must stop this, not the next attempt.
    const stillBlocked = firstBlocker(editor.checks);
    if (stillBlocked !== null) throw new Error(`Még nem indítható: ${stillBlocked.detail}`);
    const { atLocal, overrides } = scheduleOf(timing, countries, zoneOf);
    if (detail.state === "draft") await requestGroupReview(editor.groupId);
    try {
      await scheduleGroup(editor.groupId, atLocal, overrides);
    } catch (raised: unknown) {
      await editor.reload();
      throw new Error(
        `${messageOf(raised)} — A kampány most ellenőrzésre vár, a tartalma le van zárva; a „Vissza szerkesztésre” gomb nyitja meg újra.`,
      );
    }
    forgetTiming(editor.groupId);
    if (timing.mode === "now") {
      toast.success("A kiküldés elindult", {
        description: `${intoCountries(countries.length)}; az országok a következő percekben indulnak, a küldési ablakukon belül.`,
      });
    } else {
      toast.success("Az ütemezés elkészült", {
        description: `${intoCountries(countries.length)}, ${when}.`,
      });
    }
    navigate(`/kampanyok/${editor.groupId}`);
  }

  return (
    <div className="flex flex-col gap-6">
      {!composing && (
        <Callout tone="warn">
          Ez a kampány már nem vázlat (<StatusPill domain="campaignGroup" state={summary.state} />
          ). Az állapota és a számai a{" "}
          <Link to={`/kampanyok/${editor.groupId}`}>kampány oldalán</Link> látszanak.
        </Callout>
      )}

      {composing && (
        <Card>
          <CardHeader>
            <CardTitle>Ellenőrzőlista</CardTitle>
            <CardDescription>
              A kiküldés gomb addig nem nyomható meg, amíg egy piros vagy szürke tétel van a
              listán. A sárga figyelmeztetés nem akadály.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CheckList checks={checks} onGo={editor.go} />
          </CardContent>
        </Card>
      )}

      <CountriesCard editor={editor} rows={rows} />
      <PreviewCard editor={editor} rows={rows} />
      {composing && <TestSendCard editor={editor} countries={countries} />}

      {composing && (
        <Card className="border-primary/30">
          <CardHeader>
            <CardTitle>Kiküldés</CardTitle>
            <CardDescription>
              {countries.length} ország · {estimate.rows === null ? "a becslés készül" : `${count(recipients)} becsült címzett`}{" "}
              · {timing.mode === "now" ? "indulás most" : `indulás ${when}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-start gap-2">
            <Button
              type="button"
              className="h-auto min-h-control px-5 py-2 text-section whitespace-normal"
              disabled={blocker !== null}
              onClick={() => setConfirming(true)}
            >
              <SendIcon />
              {actionLabel}
            </Button>
            {blocker !== null ? (
              <p className="max-w-prose text-muted-foreground" data-slot="send-blocked">
                <span className="font-medium text-ink">Még nem indítható:</span> {blocker.detail}
              </p>
            ) : (
              <p className="max-w-prose text-muted-foreground">
                A gomb még nem küld: előbb egy ablakban megmutatja, mi történik, és ott kell
                megerősíteni.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <ActionDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={timing.mode === "now" ? "Kiküldés indítása" : "Kiküldés ütemezése"}
        description={
          <>
            A levél {intoCountries(countries.length)} megy ki, összesen{" "}
            <strong className="font-semibold text-ink">{count(recipients)}</strong> becsült
            címzettnek, a kizárások után. A kiküldött levél nem vonható vissza; a kiküldés a
            kampány oldalán bármikor szüneteltethető vagy leállítható.
          </>
        }
        confirmLabel={actionLabel}
        onConfirm={launch}
      >
        <DataTable
          label="Amit a megerősítés elindít"
          variant="plain"
          className="max-h-72 overflow-y-auto rounded-card border border-line"
          data={rows}
          getRowId={(row) => row.country}
          columns={[
            {
              id: "country",
              header: "Ország",
              cell: ({ row }) => <Country code={row.original.country} />,
            },
            {
              id: "recipients",
              header: "Címzett",
              cell: ({ row }) => count(estimated.get(row.original.country)?.recipients),
              meta: { align: "right" },
            },
            {
              id: "when",
              header: "Indulás",
              cell: ({ row }) => draftStartText(timing, row.original.country, zoneOf(row.original.country)),
              meta: { mono: true },
            },
          ]}
        />
      </ActionDialog>
    </div>
  );
}

// --- per country -------------------------------------------------------------------

function CountriesCard({ editor, rows }: { editor: Editor; rows: GroupCountryDetail[] }) {
  const { summary, estimate, timing, identityOf, renders, localizations, wording } = editor;
  const estimated = new Map((estimate.rows ?? []).map((row) => [row.country, row] as const));
  // Per SES account, only the countries this group sends to.
  const days = accountDays((estimate.rows ?? []).filter((row) => rows.some((own) => own.country === row.country)));
  const localized = new Map((localizations ?? []).map((row) => [row.country, row] as const));
  const worded = new Map((wording ?? []).map((row) => [row.country, row] as const));
  const zoneOf = (country: string) => identityOf(country)?.timezone ?? "UTC";

  const columns: ColumnDef<GroupCountryDetail>[] = [
    {
      id: "country",
      header: "Ország",
      cell: ({ row }) => (
        <span className="flex flex-col">
          <Country code={row.original.country} />
          <span className="text-label text-muted-foreground">{identityOf(row.original.country)?.label}</span>
        </span>
      ),
    },
    {
      id: "sender",
      header: "Feladó",
      cell: ({ row }) => {
        const identity = identityOf(row.original.country);
        if (identity === null) return "—";
        return (
          <span className="flex flex-col">
            <span>{identity.sender_name}</span>
            <span className="data-mono text-label text-muted-foreground">{identity.sender_email}</span>
          </span>
        );
      },
    },
    {
      // The audience's recipients, and who inside the audience is left out and
      // why (UI-11): somebody outside the audience is not "kimarad".
      id: "recipients",
      header: "Címzett",
      cell: ({ row }) => {
        const found = estimated.get(row.original.country);
        if (found === undefined) return estimate.loading ? "…" : "—";
        const figures = dayFigures(found);
        return (
          <span className="flex flex-col items-end">
            <span className="font-semibold">{count(figures.recipients)}</span>
            <span className="text-right text-label text-muted-foreground">{figures.leftOutText}</span>
          </span>
        );
      },
      meta: { align: "right", wrap: true },
    },
    {
      // The country's part of its SES account's day, at most its recipients.
      id: "today",
      header: "Ma kimegy",
      cell: ({ row }) => {
        const found = estimated.get(row.original.country);
        if (found === undefined) return "—";
        return <span className="font-semibold">{count(dayFigures(found).today)}</span>;
      },
      meta: { align: "right" },
    },
    {
      id: "after",
      header: "Utána",
      cell: ({ row }) => {
        const found = estimated.get(row.original.country);
        if (found === undefined) return "—";
        const { after } = dayFigures(found);
        return after > 0 ? count(after) : <span className="text-muted-foreground">—</span>;
      },
      meta: { align: "right" },
    },
    {
      id: "subject",
      header: "Tárgy",
      cell: ({ row }) => {
        const country = row.original.country;
        if (summary.ab !== null && summary.ab_arms.length > 0) {
          // A localised country sends its own approved cell of each arm.
          const own = summary.mode === "localized" ? worded.get(country) : undefined;
          return (
            <span className="flex flex-col gap-0.5">
              {summary.ab_arms.map((arm) => (
                <span key={arm.key}>
                  <span className="data-mono text-muted-foreground">{arm.key}</span>{" "}
                  {(own ? own.arms.find((cell) => cell.key === arm.key)?.subject : arm.subject) || "—"}
                </span>
              ))}
              <span className="text-label text-muted-foreground">a maradék a győztessel</span>
            </span>
          );
        }
        if (summary.mode === "localized") {
          const text = localized.get(country);
          return text?.subject ? text.subject : <span className="text-muted-foreground">nincs szöveg</span>;
        }
        const render = renders[country];
        if (render === undefined || render.status === "loading") return <span className="text-muted-foreground">…</span>;
        if (render.status === "error") return <span className="text-danger">nem renderelhető</span>;
        return render.preview.subject || <span className="text-danger">üres</span>;
      },
      meta: { wrap: true },
    },
    {
      id: "when",
      header: "Indulás",
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span className="data-mono">{draftStartText(timing, row.original.country, zoneOf(row.original.country))}</span>
        </span>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Országonként</CardTitle>
        <CardDescription>
          A címzett a becslés száma, a kizárások után. A feladó a küldő fiókból jön, a kampány nem
          írhatja felül.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          label="Országonként"
          variant="plain"
          className="-mx-5.5 border-t border-separator"
          columns={columns}
          data={rows}
          getRowId={(row) => row.country}
          empty={<p className="px-4 py-6 text-center text-muted-foreground">Nincs célország.</p>}
        />
        {days.length > 0 && (
          <div className="mt-4 flex flex-col gap-1">
            {days.map((day) => (
              <p key={day.account} className="data-mono">
                {accountDayText(day)}
              </p>
            ))}
            <p className="text-label text-muted-foreground">
              Becslés: a napi részeket az ütemezéskor újraszámoljuk; ami ma nem fér bele, a következő
              napokon megy ki.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- the letter, as it will render --------------------------------------------------

function PreviewCard({ editor, rows }: { editor: Editor; rows: GroupCountryDetail[] }) {
  const { renders, summary, wording } = editor;
  const [country, setCountry] = useState<string>(rows[0]?.country ?? "");
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const shown = rows.some((row) => row.country === country) ? country : (rows[0]?.country ?? "");
  const render = renders[shown];

  if (summary.letter === null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Előnézet</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Nincs levél kiválasztva.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Előnézet</CardTitle>
        <CardDescription>
          A levél úgy, ahogy a kiküldés rendereli az adott országnak: {summary.letter.template_name}{" "}
          v{summary.letter.version}, az ország nyelvén, a kötelező lábléccel.
        </CardDescription>
        <CardAction>
          <Tabs value={device} onValueChange={(value) => setDevice(value === "mobile" ? "mobile" : "desktop")}>
            <TabsList variant="segmented" aria-label="Nézet">
              <TabsTrigger value="desktop">
                <MonitorIcon className="size-4" />
                Asztali
              </TabsTrigger>
              <TabsTrigger value="mobile">
                <SmartphoneIcon className="size-4" />
                Mobil
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {rows.length > 1 && (
          <Tabs value={shown} onValueChange={setCountry}>
            <TabsList aria-label="Ország">
              {rows.map((row) => (
                <TabsTrigger key={row.country} value={row.country} className="data-mono">
                  {row.country}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}
        {render === undefined || render.status === "loading" ? (
          <LoadingState lines={4} label="Az előnézet készül…" />
        ) : render.status === "error" ? (
          <ErrorState title={`${shown}: a levél nem renderelhető`} message={render.message} />
        ) : (
          <>
            {/* In an A/B group nobody gets the letter's own subject: each
                arm's recipients get the arm's, the remainder the winner's. */}
            {subjectsSent(
              summary,
              wording?.find((entry) => entry.country === shown),
              render.preview.subject,
            ).map(({ key, subject }) => (
              <p key={key ?? "letter"} className="flex flex-wrap items-baseline gap-2">
                <span className="caps-label">{key === null ? "Tárgy" : `Tárgy · ${key}`}</span>
                <span className="font-medium">{subject || "—"}</span>
              </p>
            ))}
            {summary.ab !== null && summary.ab_arms.length > 0 && (
              <p className="text-label text-muted-foreground">
                A teszt a variánsok tárgyával megy ki, a maradék a győztesével.
              </p>
            )}
            {render.preview.warnings.map((warning) => (
              <Callout key={warning} tone="warn">
                {warning}
              </Callout>
            ))}
            <div className="overflow-x-auto rounded-card border border-line bg-surface-2 p-4">
              <iframe
                title={`Előnézet: ${shown}, ${device === "mobile" ? "mobil" : "asztali"}`}
                sandbox=""
                srcDoc={render.preview.html}
                className={cn(
                  "mx-auto block h-160 rounded-control border border-line bg-surface",
                  device === "mobile" ? "w-94" : "w-full max-w-160",
                )}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// --- the test send ------------------------------------------------------------------

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TEST_MAX = 5;
const POLL_MS = 4_000;
// A test send that has not finished in three minutes is the worker's story,
// not this page's: the pill stays where it was and the page stops asking.
const POLL_FOR_MS = 180_000;

function TestSendCard({ editor, countries }: { editor: Editor; countries: string[] }) {
  const [text, setText] = useState("");
  const [picked, setPicked] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sends, setSends] = useState<{ country: string; send: TemplateTestSend | null; id: number; to: string[] }[]>([]);
  const [pollUntil, setPollUntil] = useState(0);
  // A/B: one test letter per arm too, each under that arm's subject line.
  const [perArm, setPerArm] = useState(false);

  const chosen = picked ?? countries;
  const addresses = useMemo(
    () =>
      text
        .split(/[\s,;]+/)
        .map((entry) => entry.trim())
        .filter(Boolean),
    [text],
  );
  const bad = addresses.filter((entry) => !EMAIL.test(entry));
  const problem =
    addresses.length === 0
      ? "Adj meg legalább egy címet."
      : bad.length > 0
        ? `Nem email-cím: ${bad.join(", ")}`
        : addresses.length > TEST_MAX
          ? `Egyszerre legfeljebb ${TEST_MAX} cím.`
          : chosen.length === 0
            ? "Válassz legalább egy országot."
            : null;

  // Follow each test send until it is sent or failed.
  useEffect(() => {
    const open = sends.filter((row) => row.send === null || row.send.state === "requested" || row.send.state === "sending");
    if (open.length === 0 || Date.now() > pollUntil) return undefined;
    const timer = window.setTimeout(() => {
      void Promise.all(
        open.map((row) =>
          fetchTestSend(row.id)
            .then((send) => ({ id: row.id, send }))
            .catch(() => null),
        ),
      ).then((answers) => {
        setSends((current) =>
          current.map((row) => {
            const answer = answers.find((entry) => entry?.id === row.id);
            return answer ? { ...row, send: answer.send } : row;
          }),
        );
      });
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [sends, pollUntil]);

  async function send() {
    if (problem !== null) return;
    setBusy(true);
    setError(null);
    try {
      const arms = perArm ? editor.summary.ab_arms.map((arm) => arm.key) : [];
      const answer = await groupTestSend(editor.groupId, addresses, chosen, arms);
      setPollUntil(Date.now() + POLL_FOR_MS);
      setSends(
        answer.map((row) => ({
          country: row.arm ? `${row.country}/${row.arm}` : row.country,
          id: row.test_send_id,
          to: row.to,
          send: null,
        })),
      );
      toast.success("A teszt-levél küldése elindult", {
        description: `${answer.map((row) => (row.arm ? `${row.country}/${row.arm}` : row.country)).join(", ")} → ${addresses.join(", ")}`,
      });
    } catch (raised: unknown) {
      setError(messageOf(raised));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Teszt-levél</CardTitle>
        <CardDescription>
          Országonként egy valódi levél a megadott címekre, az ország levelével és nyelvén, a
          kötelező lábléccel — ugyanazon az úton, amin a kampány megy. Csak az engedélyezett
          (belső) domainekre küldhető.
        </CardDescription>
        <CardAction>
          <MailIcon className="size-4 text-muted-foreground" aria-hidden="true" />
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {error !== null && <ErrorState message={error} />}
        <Field
          label={`Címek (legfeljebb ${TEST_MAX})`}
          htmlFor="test-send-to"
          hint="Vesszővel vagy szóközzel elválasztva."
          className="max-w-prose"
        >
          <Input
            id="test-send-to"
            value={text}
            placeholder="marketing@mobilfox.hu"
            onChange={(event) => setText(event.target.value)}
          />
        </Field>
        <fieldset className="m-0 flex flex-wrap gap-x-4 gap-y-2 border-0 p-0">
          <legend className="mb-1.5 font-medium">Országok</legend>
          {countries.map((country) => (
            <label key={country} className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={chosen.includes(country)}
                onChange={(event) =>
                  setPicked(
                    event.target.checked
                      ? [...chosen, country]
                      : chosen.filter((code) => code !== country),
                  )
                }
              />
              <span className="data-mono">{country}</span>
            </label>
          ))}
        </fieldset>
        {editor.summary.ab !== null && editor.summary.ab_arms.length > 0 && (
          <label className="flex cursor-pointer items-center gap-2">
            <Checkbox checked={perArm} onChange={(event) => setPerArm(event.target.checked)} />
            Minden A/B-variánsból egy-egy levél, a variáns tárgyával
          </label>
        )}
        <div className="flex flex-col items-start gap-1.5">
          <Button type="button" variant="outline" disabled={busy || problem !== null} onClick={() => void send()}>
            <MailIcon />
            {busy ? "Küldés…" : "Teszt-levél küldése"}
          </Button>
          {problem !== null && text !== "" && <p className="text-label text-muted-foreground">{problem}</p>}
        </div>
        {sends.length > 0 && (
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0" aria-label="Teszt-levelek">
            {sends.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center gap-2">
                <span className="data-mono font-medium">{row.country}</span>
                <StatusPill domain="testSend" state={row.send?.state ?? "requested"} />
                <span className="text-muted-foreground">{row.to.join(", ")}</span>
                {row.send?.error && <span className="text-danger">{row.send.error}</span>}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
